import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  ButtonResponsiveWrapper,
  FiservCardStatusEnum,
  Hint,
  Text,
  useIsCardMailWithin7Days,
  useResponsive,
  WarningIcon,
} from '@common-fe/common-fe';
import dayjs from 'dayjs';
import Lottie from 'lottie-react';

import BankCardIcon from '@/assets/bank-icons/card.json';
import CardNumbersIcon from '@/assets/bank-icons/card-numbers.json';
import { DEFAULT_DATE_FORMAT } from '@/common/constants';
import AppButton from '@/components/controls/AppButton';
import { Banner } from '@/modules/core/components/Dashboard/components/BannersList/Banner';
import useCardReplaceModalStore from '@/modules/transaction/components/Cards/store/useCardReplaceModal.store.ts';
import { useGetCardsQuery } from '@/modules/transaction/queries/useGetCards.query';
import { LimitedAccessVerificationButton } from '@/modules/user/components/LimitedAccess';
import { useAuthStore } from '@/modules/user/stores';
import theme from '@/styles/theme';

import useCardHolderQuery from './useCardHolder.query';
import VerifyEmployeeButton from './VerifyEmployeeButton';

const BUTTON_WIDTH = '180px';

interface Props {
  handleOpenModal: () => void;
  isWarningMode?: boolean;
}

const CompleteBanner: React.FC<Props> = ({ handleOpenModal, isWarningMode }) => {
  const { isMobile } = useResponsive();
  const { user } = useAuthStore();
  const { t } = useTranslation();
  const { mailedDataOfLastCard, isPendingCardExists } = useCardHolderQuery();
  const { employeePendingOpenCard } = useGetCardsQuery(user?.employee?.id);
  const isMailWithin7Days = useIsCardMailWithin7Days(employeePendingOpenCard);

  const isCardMailed = useMemo(() => mailedDataOfLastCard, [mailedDataOfLastCard]);

  const {
    handleChangeVisibility,
    handleChangeCardHolderName,
    handleChangeCardHolderID,
    handleChangeCardID,
    handleChangeCardStatus,
    handleChangeMailedDate,
    handleChangeActivationDate,
  } = useCardReplaceModalStore();

  const setCardInfo = useCallback(() => {
    handleChangeCardHolderName(employeePendingOpenCard?.holder?.name || '');
    handleChangeCardHolderID(employeePendingOpenCard?.holder.employeeId || '');
    handleChangeCardID(employeePendingOpenCard?.id || '');
    handleChangeCardStatus(employeePendingOpenCard?.status);
    handleChangeMailedDate(employeePendingOpenCard?.mailedDate || null);
    handleChangeActivationDate(employeePendingOpenCard?.activationDate || null);
  }, [
    handleChangeCardHolderID,
    handleChangeCardHolderName,
    handleChangeCardStatus,
    handleChangeCardID,
    employeePendingOpenCard,
    handleChangeMailedDate,
    handleChangeActivationDate,
  ]);

  const onCardReplacementModal = useCallback(() => {
    setCardInfo();
    handleChangeVisibility(true);
  }, [handleChangeVisibility, setCardInfo]);

  const waitToBeMailedDate = useMemo(() => {
    const date = employeePendingOpenCard?.mailedDate;
    if (date) {
      return dayjs(date).add(7, 'days').format(DEFAULT_DATE_FORMAT);
    }
    return null;
  }, [employeePendingOpenCard?.mailedDate]);

  const bannerTitle = useMemo(() => {
    return t('Action Required');
  }, [t]);

  const isCardRequestStatus = useMemo(() => {
    return isPendingCardExists && !isCardMailed;
  }, [isCardMailed, isPendingCardExists]);

  const isCardOnWayStatus = useMemo(() => {
    return isPendingCardExists && !!isCardMailed && isMailWithin7Days;
  }, [isCardMailed, isMailWithin7Days, isPendingCardExists]);

  const isCardDeliveredStatus = useMemo(() => {
    return isPendingCardExists && !!isCardMailed && !isMailWithin7Days;
  }, [isCardMailed, isMailWithin7Days, isPendingCardExists]);

  const bannerText = useMemo(() => {
    if (isCardOnWayStatus || isCardDeliveredStatus || !isPendingCardExists) {
      return (
        <Box direction="column">
          <Text weight={400}>
            {t(
              'Your Account is Limited. Online and mobile features are locked until you verify your identity. Verify now to unlock full access.'
            )}
          </Text>

          <Text weight={400} margin={{ top: 'spacing8' }}>
            <Text weight={700}>{t('Why is this required? ')}</Text>
            {t('This step protects your funds and helps prevent unauthorized access.')}
            <Text id="banner-verification-portal"></Text>
          </Text>
        </Box>
      );
    }

    if (isCardRequestStatus || (!isCardDeliveredStatus && !isCardOnWayStatus)) {
      return t(
        'A debit card will be mailed to you shortly. For your security, you must verify your card before you can begin using your account.'
      );
    }
  }, [isCardDeliveredStatus, isCardOnWayStatus, isCardRequestStatus, isPendingCardExists, t]);

  const bannerRequestHint = useMemo(() => {
    if (isPendingCardExists && isMailWithin7Days && isCardMailed) {
      return (
        <Text size="small">
          {`${t(
            "Your card is on its way and should arrive soon. If you don't receive it by"
          )} ${waitToBeMailedDate}${t(', you can request a replacement.')} `}
        </Text>
      );
    }
    if (isCardRequestStatus) {
      return (
        <Text size="small">
          {t('You can request a card replacement in 7 days after card has been sent to you. Please wait.')}
        </Text>
      );
    }

    return null;
  }, [isCardMailed, isCardRequestStatus, isMailWithin7Days, isPendingCardExists, t, waitToBeMailedDate]);
  if (isWarningMode) {
    return (
      <Box
        border={{ size: 'small', color: 'warningBorder' }}
        direction={isMobile ? 'column' : 'row'}
        align="center"
        round="container1Round"
        background="warningContainer"
        pad={{ vertical: 'l', horizontal: 'xl' }}
      >
        <div>
          <Box margin={{ right: 's' }}>
            <WarningIcon size="24px" color="warning" />
          </Box>
        </div>
        <Box fill pad={{ right: 'l' }}>
          <Text size="large" weight={700}>
            {t(`To keep your account safe, we require a quick verification step before allowing full access.
This added level of security helps protect your funds and personal information. Please verify your card to access your account documents.`)}
          </Text>
        </Box>
        <div>
          <VerifyEmployeeButton handleOpenModal={handleOpenModal} />
        </div>
      </Box>
    );
  }

  return (
    <Banner
      title={bannerTitle}
      description={bannerText as string}
      height={isMobile && (isCardOnWayStatus || isCardDeliveredStatus) ? { min: '400px' } : undefined}
      icon={
        <Lottie animationData={isCardMailed && mailedDataOfLastCard ? CardNumbersIcon : BankCardIcon} loop />
      }
    >
      <div>
        <ButtonResponsiveWrapper isReversedMode margin={{ top: 'spacing24' }}>
          <Box fill={isMobile} width="180px">
            <LimitedAccessVerificationButton
              isBannerMode
              size="M"
              fill={isMobile}
              width={isMobile ? '100%' : '180px'}
            >
              {undefined}
            </LimitedAccessVerificationButton>
          </Box>
          {employeePendingOpenCard?.status === FiservCardStatusEnum.PENDING_OPEN && (
            <Hint
              hintWidth={isMobile ? 'calc(100% - 24px)' : 240}
              hideHint={!isMailWithin7Days && isCardMailed}
              disabled={!isCardMailed || isMailWithin7Days}
              backgroundColor={theme.colors.background3}
              hintElement={
                <Box>
                  <AppButton
                    fill={isMobile}
                    testId="request-new-card"
                    width={BUTTON_WIDTH}
                    style={{
                      height: '40px',
                    }}
                    buttonType={isMobile ? 'secondary' : 'quaternary'}
                    disabled={!isCardMailed || isMailWithin7Days}
                    onClick={onCardReplacementModal}
                  >
                    {t('Request a New Card')}
                  </AppButton>
                </Box>
              }
            >
              {bannerRequestHint}
            </Hint>
          )}
        </ButtonResponsiveWrapper>
      </div>
    </Banner>
  );
};

export default CompleteBanner;
